/**
 * Seat allotment is a three-state fact, not a number with sentinels.
 *
 *   unknown   — the server could not assert a seat allotment (unresolved
 *               billing, unrecognized tier). The UI must say so; it must never
 *               render "0 seats" or silently substitute a tier default.
 *   finite    — a known positive integer allotment.
 *   unlimited — contractually unlimited (enterprise). Never `Infinity` in the
 *               wire model, never a number in the UI.
 *
 * `0` and `Infinity` are never valid seat values in this model.
 */

export type SeatAllotment =
  | { kind: 'unknown' }
  | { kind: 'finite'; seats: number }
  | { kind: 'unlimited' };

export const UNKNOWN_SEATS: SeatAllotment = { kind: 'unknown' };

/** Build an allotment from the server wire fields. */
export function seatAllotmentFromServer(input: {
  seats: number | null | undefined;
  seatsUnlimited?: boolean | null;
}): SeatAllotment {
  if (input.seatsUnlimited) return { kind: 'unlimited' };
  const seats = input.seats;
  if (typeof seats === 'number' && Number.isFinite(seats) && seats > 0) {
    return { kind: 'finite', seats };
  }
  return { kind: 'unknown' };
}

/** Build an allotment from the static tier table (which uses Infinity). */
export function seatAllotmentFromTierTable(
  seats: number | null | undefined,
): SeatAllotment {
  if (seats === Infinity) return { kind: 'unlimited' };
  if (typeof seats === 'number' && Number.isFinite(seats) && seats > 0) {
    return { kind: 'finite', seats };
  }
  return { kind: 'unknown' };
}

/**
 * Server allotment wins; the static tier table is used only when the server
 * asserted nothing. An unlimited tier stays unlimited in both directions.
 */
export function resolveSeatAllotment(
  server: SeatAllotment,
  tierFallback: SeatAllotment,
): SeatAllotment {
  return server.kind === 'unknown' ? tierFallback : server;
}

export function formatSeatAllotment(allotment: SeatAllotment): string {
  switch (allotment.kind) {
    case 'unlimited':
      return 'Unlimited seats';
    case 'finite':
      return `${allotment.seats} seat${allotment.seats === 1 ? '' : 's'}`;
    default:
      return 'Seat count unavailable';
  }
}

/** "3 / 10 seats", "3 members (unlimited seats)", "3 members" */
export function formatSeatUsage(
  allotment: SeatAllotment,
  used: number,
): string {
  switch (allotment.kind) {
    case 'unlimited':
      return `${used} member${used === 1 ? '' : 's'} (unlimited seats)`;
    case 'finite':
      return `${used} / ${allotment.seats} seats`;
    default:
      return `${used} member${used === 1 ? '' : 's'}`;
  }
}

/**
 * Usage ratio in [0, 1] for the seat meter, or null when no meter should be
 * drawn. Divide-by-zero is structurally impossible: only `finite` allotments
 * (always > 0) produce a ratio.
 */
export function seatUsageRatio(
  allotment: SeatAllotment,
  used: number,
): number | null {
  if (allotment.kind !== 'finite') return null;
  const ratio = used / allotment.seats;
  if (!Number.isFinite(ratio) || ratio < 0) return null;
  return Math.min(ratio, 1);
}

/** True only when a finite allotment is known to be fully consumed. */
export function seatsExhausted(
  allotment: SeatAllotment,
  used: number,
): boolean {
  return allotment.kind === 'finite' && used >= allotment.seats;
}

/**
 * Billed seats exclude protected Architect (platform-owner) participation,
 * which is administrative presence, not a customer seat. Pure presentation of
 * the roster already read; never an authorization input.
 */
export function billedSeatCount(members: ReadonlyArray<{ role?: string | null; isActive?: boolean | null; is_active?: boolean | null }>): number {
  return members.filter((m) => (m.isActive ?? m.is_active ?? true) !== false && m.role !== 'architect').length;
}
